import React, { useState, useEffect } from 'react';
import { withStyles, Button } from '@material-ui/core';
import { AgGrid } from '../../../components/AgGrid';
import classnames from 'classnames';
import { addDays, addMonths, startOfMonth, subDays, format, differenceInCalendarDays } from 'date-fns';
import LinearProgress from '@material-ui/core/LinearProgress';
import { getReportPeriod, getPeriodRange, generatePeriodLabel } from '../../../helpers/constants/payroll';
import { Autocomplete } from '../../../components/Autocomplete';
import DatePicker from '../../../components/DatePicker';
import Checkbox from '@material-ui/core/Checkbox';

// SERVICES
import {
  getIndividualsPaymentReport,
  confirmIndividualsPaymentReport,
  individualsPaymentReportExists,
} from '../../../services';

const styles = theme => ({
  inputForm: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 20,
  },

  checkbox: {
    color: 'var(--jmi-blue) !important',
    marginLeft: 5,
    marginRight: 5,
    '&:hover': {
      backgroundColor: 'rgba(47, 93, 150, 0.2) !important',
    },
  },
});

const today = new Date();

export const IndividualAccounting = withStyles(styles)(props => {
  const { classes, territoryBased, reportType } = props;

  const REPORT_TYPE = reportType;

  const [endDay, setEndDay] = useState(null);
  const [gridData, setGridData] = useState(null);
  // const [reportPeriods, setReportPeriods] = useState(generateRecentReportPeriods);
  const [reportPeriod, setReportPeriod] = useState(getReportPeriod);

  // period range
  const [reportPeriodStartDate, setReportPeriodStartDate] = useState(null);
  const [reportPeriodEndDate, setReportPeriodEndDate] = useState(null);
  const [isPartialPeriod, setIsPartialPeriod] = useState(false);
  const [usePeriodLock, setUsePeriodLock] = useState(true);

  const [showGrid, setShowGrid] = useState(false);
  const [reportAlreadyExists, setReportAlreadyExists] = useState(false);
  const [lastCompletedThroughDate, setLastCompletedThroughDate] = useState(null);
  const [noDataMessage, setNoDataMessage] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isSendingConfirmation, setIsSendingConfirmation] = useState(false);
  const [reportIsConfirmed, setReportIsConfirmed] = useState(null);

  const gridColDefs = [
    { headerName: 'Name', field: 'name', width: 500 },
    { headerName: '# of Orders', field: 'orderLineCount' },
    { headerName: 'Total Payment ($)', field: 'orderLineTotal' },
  ];

  const updateEndDate = date => {
    // do not allow selection of a date equal to or after today
    const diffInDaysEnd = differenceInCalendarDays(today, date);

    if (diffInDaysEnd <= 0) return;

    // do not allow selection of a date less than the start date
    const diffInDaysStart = differenceInCalendarDays(date, reportPeriodStartDate);
    if (diffInDaysStart < 0) return;

    setReportPeriodEndDate(date);
  };

  const updatePeriodLockCheckbox = evt => {
    const checked = !usePeriodLock;

    if (checked) {
      // reset the end date to the end of the next period (or end of partial period)
      // const daysToAdd = isPartialPeriod ? differenceInCalendarDays(new Date(), reportPeriodStartDate) - 1 : 6;
      const daysToAdd = 6;
      setReportPeriodEndDate(addDays(reportPeriodStartDate, daysToAdd));
    }

    if (!checked && isPartialPeriod) {
      // make sure the start date isn't today, otherwise keep it the same
      const isSameDay = !differenceInCalendarDays(new Date(), reportPeriodStartDate);
      setReportPeriodEndDate(subDays(new Date(), isSameDay ? 0 : 1));
    }
    setUsePeriodLock(checked);
  };

  const getIndividualsPaymentReportAsync = async () => {
    setGridData(null);
    // const startDate = reportPeriodStartDate.toUTCString();
    // const endDate = addDays(reportPeriodEndDate, 1).toUTCString();

    // return console.log(startDate, endDate);
    // const startDate = new Date(start).toUTCString();
    // const endDate = new Date(end).toUTCString();

    try {
      // const { endDate, label } = reportPeriod;
      const label = generatePeriodLabel(reportPeriodStartDate, reportPeriodEndDate);

      setIsGeneratingReport(true);
      // const res = await getIndividualsPaymentReport(endDate, label, addDays(endDate, 30), REPORT_TYPE);

      // add one day to the end date since the end date is exclusive
      const res = await getIndividualsPaymentReport(
        addDays(reportPeriodEndDate, 1),
        label,
        addDays(reportPeriodEndDate, 30),
        REPORT_TYPE,
      );
      setIsGeneratingReport(false);

      if (res.results.length) {
        setShowGrid(true);
        return setGridData(res.results);
      }

      setNoDataMessage(true);
    } catch (error) {
      console.log(error);
    }
  };

  const sendConfirmation = async (confirm = false) => {
    try {
      setIsSendingConfirmation(true);
      setReportIsConfirmed(confirm);
      setShowGrid(false);
      const res = await confirmIndividualsPaymentReport(confirm, REPORT_TYPE);
      setIsSendingConfirmation(false);
      setReportIsConfirmed(null);
      setGridData(null);
      setReportAlreadyExists(false);
      setNoDataMessage(false);

      // refetch the data for last completed date, etc
      individualsPaymentReportExistsAsync();
    } catch (error) {
      console.log(error);
    }
  };

  const getExistingIndividualsPaymentReportAsync = async () => {
    try {
      const res = await getIndividualsPaymentReport(null, null, null, REPORT_TYPE);
      setGridData(res.results);
      setShowGrid(true);
      setReportAlreadyExists(false);
      setNoDataMessage(true);
    } catch (error) {
      console.log(error);
    }
  };

  const goToHistory = () => {
    window.location = window.location.href + '/history';
  };

  const individualsPaymentReportExistsAsync = async () => {
    try {
      const res = await individualsPaymentReportExists(REPORT_TYPE);
      setReportAlreadyExists(res.reportExists);
      const formattedToDate = format(subDays(new Date(res.lastCompletedToDate), 1), 'MM/dd/yyyy');
      setLastCompletedThroughDate(formattedToDate);

      // calculate the date range to set based on the last completed date
      const range = getPeriodRange(new Date(res.lastCompletedToDate));
      setReportPeriodStartDate(range.startDate);
      setReportPeriodEndDate(range.endDate);
      setIsPartialPeriod(range.isPartialPeriod);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    individualsPaymentReportExistsAsync();
  }, []);

  return (
    <div className={classnames('flex-1', 'card', 'border-left-green', 'col-center-start', 'align-stretch')}>
      <div className={classnames(classes.inputForm, 'row-between-center')}>
        {/* show the month that the report is pulled for */}
        <div className={classnames('column-start-start')}>
          <div className={classnames('row-start-center')}>
            <span className={classnames('mr-15', 'text-left')} style={{ width: 75 }}>
              From:
            </span>
            <DatePicker disabled selected={reportPeriodStartDate} onChange={setReportPeriodStartDate} />
          </div>
          <div className={classnames('row-start-center')}>
            <span className={classnames('mr-15', 'text-left')} style={{ width: 75 }}>
              Through:
            </span>
            <DatePicker disabled={usePeriodLock} selected={reportPeriodEndDate} onChange={updateEndDate} />
            <Checkbox checked={usePeriodLock} onChange={updatePeriodLockCheckbox} className={classes.checkbox} />
            <span style={{ opacity: 0.5 }}>Use 7-day period</span>
          </div>
          {/* {isPartialPeriod && <div style={{ opacity: 0.5 }}>This will run as a partial period</div>} */}
          {isPartialPeriod && usePeriodLock && (
            <div style={{ opacity: 0.5, width: '70%' }}>
              The most recent period has not ended yet. In order to run a partial period, uncheck the 7-day period
              checkbox above.
            </div>
          )}
        </div>

        <div className={classnames('col-start-end')} style={{ height: '100%' }}>
          <div className={classnames('mb-15', 'text-right')}>
            Last report completed through&nbsp;
            <span className={classnames('text-bold')}>{lastCompletedThroughDate}</span>
          </div>

          {!showGrid && !reportAlreadyExists && (
            <div className={classnames('row-end-center')}>
              <Button
                variant="contained"
                style={{ backgroundColor: 'var(--jmi-blue)', color: 'white', marginRight: 20 }}
                onClick={goToHistory}
              >
                View History
              </Button>
              <Button
                variant="contained"
                style={{ backgroundColor: isPartialPeriod && usePeriodLock ? '#AAA' : '#4CAF50', color: 'white' }}
                onClick={getIndividualsPaymentReportAsync}
                disabled={isGeneratingReport || isSendingConfirmation || (isPartialPeriod && usePeriodLock)}
              >
                Run Payroll
              </Button>
            </div>
          )}
          {showGrid && gridData && (
            <div className={classnames('flex-1', 'row-end-center')}>
              <Button
                variant="contained"
                style={{ backgroundColor: '#F44336', color: 'white', marginRight: 20 }}
                onClick={() => sendConfirmation(false)}
              >
                Discard Report
              </Button>

              <Button
                variant="contained"
                style={{ backgroundColor: 'var(--jmi-blue)', color: 'white' }}
                onClick={() => sendConfirmation(true)}
              >
                Complete Payroll and Export Data
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className={classnames('flex-1', 'col-center-center', 'align-stretch')} style={{ background: '#ccc' }}>
        {!showGrid && (
          <div className={'col-center-center'}>
            {/* shows a message whenever the report is being generated or accepted/rejected */}
            {isGeneratingReport && (
              <div style={{ marginTop: 20, fontSize: 24 }}>
                <div style={{ marginBottom: 20 }}>Please wait while your payment report is generated</div>
                <LinearProgress />
              </div>
            )}
            {isSendingConfirmation && (
              <div style={{ marginTop: 20, fontSize: 24 }}>
                <div style={{ marginBottom: 20 }}>
                  Please wait while your payroll report is {reportIsConfirmed ? 'confirmed and exported' : 'discarded'}
                </div>
                <LinearProgress />
              </div>
            )}

            {!isGeneratingReport && !isSendingConfirmation && (
              <div className={classnames('flex-1', 'align-stretch', 'col-center-center')}>
                {!reportAlreadyExists && !noDataMessage && (
                  <div style={{ marginTop: 20, fontSize: 24 }}>Please run payroll to view report</div>
                )}
                {!reportAlreadyExists && noDataMessage && (
                  <div style={{ marginTop: 20, fontSize: 24 }}>
                    No new orders were found. No new payroll reports were created. Please check back later
                  </div>
                )}

                {reportAlreadyExists && (
                  <div style={{ width: '80%' }}>
                    <div style={{ marginBottom: 30, fontSize: 24 }}>
                      A payroll report already exists for this period... Either discard and regenerate the report or
                      view it to confirm the details
                    </div>
                    <div>
                      <Button
                        variant="contained"
                        style={{
                          backgroundColor: '#F44336',
                          color: 'white',
                          marginRight: 20,
                        }}
                        onClick={() => sendConfirmation(false)}
                      >
                        Discard Report
                      </Button>

                      <Button
                        variant="contained"
                        style={{
                          backgroundColor: 'var(--jmi-blue)',
                          color: 'white',
                          marginLeft: 20,
                        }}
                        onClick={() => getExistingIndividualsPaymentReportAsync()}
                      >
                        View Report
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {showGrid && (
          <AgGrid gridData={gridData} columnDefs={gridColDefs} redirectOnClick={false} editOnClick={false} />
        )}
      </div>
    </div>
  );
});
